import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  useTable,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';
import { pagedFeatures, type FilterFn } from '../components/common/tableFeatures';
import { exportToJSON, downloadFile } from '../utils/dataExport';
import { useGetDevicesQuery } from '@store/api/deviceApi';
import {
  useExportDpsEnrollmentCsvMutation,
  useExportDpsEnrollmentJsonMutation,
} from '@store/api/devicesApi';
import { useQueryError } from '@hooks/useQueryError';
import ColumnSelector from '../components/common/ColumnSelector';
import {
  DataTable,
  DataTablePagination,
  DataTableToolbar,
} from '../components/common/DataTable';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { EXPORT_COLUMNS, type ExportDevice } from '../components/export/exportColumns';
import {
  EXPORT_COLUMN_METADATA,
  EXPORT_DEFAULT_COLUMNS,
} from '../components/export/exportColumnMetadata';
import { useHasChanged } from '@hooks/useHasChanged';

/** Search covers exactly the three fields the placeholder advertises. The
 *  default global filter would instead match every column, including the date
 *  columns whose sort accessors are epoch numbers.
 *
 *  TanStack runs the global filter per filterable column, so only the one
 *  column below opts in - otherwise this ran nine times per row. */
const searchDevices: FilterFn<ExportDevice> = (row, _columnId, value) => {
  const term = String(value).toLowerCase();
  const device = row.original;
  return (
    device.name.toLowerCase().includes(term) ||
    (device.registrationId?.toLowerCase().includes(term) ?? false) ||
    (device.macAddress?.toLowerCase().includes(term) ?? false)
  );
};

const ExportPage: React.FC = () => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [verifiedOnly, setVerifiedOnly] = useState(true);
  const [includeValidation, setIncludeValidation] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);

  const {
    data: devicesResponse,
    isFetching: loading,
    error: fetchError,
    refetch,
  } = useGetDevicesQuery({
    pageSize: 1000,
    ...(verifiedOnly ? { registration_status: 'verified' as const } : {}),
  });
  const [exportDpsEnrollmentCsv] = useExportDpsEnrollmentCsvMutation();
  const [exportDpsEnrollmentJson] = useExportDpsEnrollmentJsonMutation();

  const queryError = useQueryError(fetchError);
  const displayError = error ?? queryError;

  const devices: ExportDevice[] = useMemo(
    () =>
      (devicesResponse?.data ?? []).map((device) => ({
        ...device,
        createdAt: device.createdAt || '',
        verifiedAt: (device as { verifiedAt?: string }).verifiedAt || '',
      })),
    [devicesResponse]
  );

  // A fresh device list drops the selection, as it did before the table kept
  // selection of its own - toggling "Verified only" must not export stale rows.
  if (useHasChanged(devicesResponse)) setRowSelection({});

  const {
    visibleColumns,
    toggleColumn,
    resetToDefault,
    applyPreset,
    columnVisibility,
  } = useColumnVisibility({
    columns: EXPORT_COLUMN_METADATA,
    storageKey: 'exportPage.columnVisibility.v1',
    defaultColumns: EXPORT_DEFAULT_COLUMNS,
  });

  const table = useTable({
    features: pagedFeatures,
    data: devices,
    columns: EXPORT_COLUMNS,
    state: {
      rowSelection,
      sorting,
      globalFilter,
      columnVisibility,
      pagination: { pageIndex: page, pageSize: rowsPerPage },
    },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: searchDevices,
  });

  const selectedDevices = table.getSelectedRowModel().rows.map((row) => row.original);
  const selectedCount = selectedDevices.length;

  const handleExport = async (format: 'csv' | 'json') => {
    if (selectedCount === 0) {
      setError('Please select at least one device to export');
      return;
    }

    setExporting(true);
    setError(null);
    setSuccess(null);

    try {
      const deviceIds = selectedDevices.map((d) => d.deviceId);

      if (format === 'csv') {
        // Use backend DPS export API for CSV format (converts PEM to DPS-compatible base64)
        const blob = await exportDpsEnrollmentCsv({ deviceIds }).unwrap();

        // Download CSV file
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `dps_enrollment_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setSuccess(
          `Successfully exported ${selectedCount} device${selectedCount === 1 ? '' : 's'} in DPS-compatible format`
        );
      } else {
        // Use backend DPS export API for JSON format (already converted to DPS format by backend)
        const jsonExport = await exportDpsEnrollmentJson({ deviceIds }).unwrap();

        // Download JSON file
        downloadFile(
          exportToJSON(jsonExport),
          `dps_enrollment_${new Date().toISOString().split('T')[0]}.json`,
          'application/json',
        );

        const exportedCount = jsonExport.export_metadata?.total_devices || selectedCount;
        const errorCount = jsonExport.export_metadata?.error_count || 0;

        setSuccess(
          `Successfully exported ${exportedCount} device${exportedCount === 1 ? '' : 's'} in DPS-compatible JSON format` +
          (errorCount > 0 ? ` (${errorCount} errors)` : '')
        );
      }
    } catch (err: any) {
      setError('Failed to generate export file');
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Device Export for Azure IoT Hub
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Export Settings
        </Typography>

        <Stack direction="row" spacing={3} sx={{
          alignItems: 'center'
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Verified Devices Only"
          />

          <FormControlLabel
            control={
              <Switch
                checked={includeValidation}
                onChange={(e) => setIncludeValidation(e.target.checked)}
                color="primary"
              />
            }
            label="Include DPS Validation"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={loading}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('csv')}
            disabled={exporting || selectedCount === 0}
          >
            Export CSV ({selectedCount})
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('json')}
            disabled={exporting || selectedCount === 0}
          >
            Export JSON ({selectedCount})
          </Button>
        </Stack>
      </Paper>

      {displayError && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {displayError}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <DataTableToolbar
          searchValue={globalFilter}
          onSearchChange={(value) => {
            setGlobalFilter(value);
            setPage(0);
          }}
          searchPlaceholder="Search name, registration ID, MAC..."
          searchAriaLabel="Search devices by name, registration ID, or MAC address"
          actions={
            <ColumnSelector
              columns={EXPORT_COLUMN_METADATA}
              visibleColumns={visibleColumns}
              onColumnToggle={toggleColumn}
              onReset={resetToDefault}
              onPresetSelect={applyPreset}
            />
          }
        />

        <DataTable
          table={table}
          ariaLabel="Devices to export"
          isLoading={loading}
          emptyMessage="No devices found"
        />

        <DataTablePagination
          count={table.getFilteredRowModel().rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(size) => {
            setRowsPerPage(size);
            setPage(0);
          }}
        />
      </Paper>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Export Information
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginBottom: '16px'
          }}>
          This page allows you to export device information for enrollment in Azure IoT Hub's Device Provisioning Service (DPS).
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginBottom: '16px'
          }}>
          <strong>Exported fields include:</strong>
        </Typography>
        <ul style={{ marginTop: 0 }}>
          <li><Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>Registration ID - Unique identifier for DPS enrollment</Typography></li>
          <li><Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>TPM Endorsement Key (EK) - Hardware security module public key (PEM format)</Typography></li>
          <li><Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>MAC Address - Network interface identifier</Typography></li>
          <li><Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>Hardware Model - Device model information</Typography></li>
          <li><Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>TPM Manufacturer - TPM chip vendor</Typography></li>
          <li><Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>Verification Status - Device verification state</Typography></li>
        </ul>
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          <strong>CSV format:</strong> Best for bulk import into Azure DPS or Excel processing<br />
          <strong>JSON format:</strong> Best for programmatic processing or API integration
        </Typography>
      </Box>
    </Box>
  );
};

export default ExportPage;
